import { useEffect, useState, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Nfc, Loader2, AlertTriangle, ChevronDown, Cpu, HardDrive, ScanLine, Power, Plus, QrCode, FileText, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PageGuideButton } from "@/components/PageGuideButton";
import { PageHeader } from "@/components/shared";
import api from "@/lib/api";

const STATUSES = ["", "NEEDS_REPROGRAMMING", "READY_FOR_PROGRAMMING", "PROGRAMMING", "PROGRAMMED", "TESTED", "READY_TO_SHIP", "ACTIVE", "INACTIVE", "REVOKED"];

export default function AdminNfcTags() {
  const { t } = useTranslation();
  const [tags, setTags] = useState(null);
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [busyTag, setBusyTag] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [sites, setSites] = useState([]);
  const [selOrg, setSelOrg] = useState("");
  const [selSite, setSelSite] = useState("");
  const [createdUrl, setCreatedUrl] = useState("");
  const [newSiteName, setNewSiteName] = useState("");

  const load = useCallback((st) => {
    setTags(null);
    api.get(`/admin/hardware/nfc-tags${st ? `?status=${st}` : ""}`).then((r) => {
      setTags(r.data.tags); setSummary(r.data.summary); setDevices(r.data.devices || []);
    }).catch(() => { setTags([]); setSummary(null); });
  }, []);
  useEffect(() => { load(status); }, [status, load]);

  const toggleActive = async (tg) => {
    setBusyTag(tg.id);
    try {
      const r = await api.post(`/admin/hardware/tags/${tg.id}/set-active`, { active: tg.status !== "ACTIVE" });
      toast.success(r.data.status === "ACTIVE" ? t("nfc.reactivated") : t("nfc.suspended"));
      load(status);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusyTag(null); }
  };

  const openCreate = async () => {
    setCreateOpen(true); setSelOrg(""); setSelSite(""); setSites([]); setCreatedUrl("");
    try { const r = await api.get("/admin/companies"); setOrgs(r.data.companies || []); } catch { /* ignore */ }
  };
  const onSelectOrg = async (id) => {
    setSelOrg(id); setSelSite(""); setSites([]);
    if (id) { try { const r = await api.get(`/admin/hardware/sites?organization_id=${id}`); setSites(r.data.sites || []); } catch { /* ignore */ } }
  };
  const createTag = async () => {
    setBusyTag("new");
    try {
      const r = await api.post("/admin/hardware/tags/manual", { organization_id: selOrg || null, site_id: selSite || null });
      setCreatedUrl(r.data.nfc_url || ""); toast.success(t("ai.testTagCreated")); load(status);
    } catch { toast.error("Error"); } finally { setBusyTag(null); }
  };
  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success(t("nfc.copied")); };
  const addSite = async () => {
    if (!newSiteName.trim() || !selOrg) return;
    try {
      const r = await api.post("/admin/hardware/sites", { organization_id: selOrg, name: newSiteName.trim() });
      const rs = await api.get(`/admin/hardware/sites?organization_id=${selOrg}`);
      setSites(rs.data.sites || []); setSelSite(r.data.id); setNewSiteName(""); toast.success(t("nfc.siteAdded"));
    } catch { toast.error("Error"); }
  };

  const download = async (url, name) => {
    try { const r = await api.get(url, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("Error"); }
  };

  const badge = (s) => ({ ACTIVE: "bg-emerald-100 text-emerald-700", INACTIVE: "bg-amber-100 text-amber-700", PROGRAMMED: "bg-cyan/15 text-cyan-dark", TESTED: "bg-cyan/15 text-cyan-dark", READY_TO_SHIP: "bg-indigo-100 text-indigo-700", REVOKED: "bg-slate-200 text-slate-600", NEEDS_REPROGRAMMING: "bg-red-100 text-red-700 ring-1 ring-red-300" }[s] || "bg-slate-100 text-slate-600");
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

  return (
    <div data-testid="admin-nfc-tags">
      <PageHeader icon={Nfc} title={t("nfc.adminTitle")} subtitle={t("nfc.adminSub")} action={<PageGuideButton pageKey="nfc-tags" />} />

      {/* Summary counters */}
      {summary && (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="nfc-summary">
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">{t("nfc.totalTags")}</div><div className="font-heading text-2xl font-extrabold text-navy" data-testid="nfc-total">{summary.total}</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">{t("nfc.activeTags")}</div><div className="font-heading text-2xl font-extrabold text-emerald-600" data-testid="nfc-active">{summary.active}</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">{t("nfc.verifiedTags")}</div><div className="font-heading text-2xl font-extrabold text-cyan-dark" data-testid="nfc-verified">{summary.verified}</div></div>
          <div className={`rounded-2xl border p-4 ${summary.needs_reprogramming > 0 ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}><div className="flex items-center gap-1 text-xs text-slate-500">{summary.needs_reprogramming > 0 && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}{t("nfc.needReprogramming")}</div><div className={`font-heading text-2xl font-extrabold ${summary.needs_reprogramming > 0 ? "text-red-600" : "text-navy"}`} data-testid="nfc-needs">{summary.needs_reprogramming}</div></div>
        </div>
      )}

      {/* Provisioning devices */}
      {devices.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid="nfc-devices">
          <span className="font-semibold uppercase tracking-wide">{t("nfc.provDevices")}:</span>
          {devices.map((d) => (
            <span key={d.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1" data-testid={`nfc-device-${d.id}`}>
              <ScanLine className="h-3.5 w-3.5 text-cyan-dark" /> {d.device_name} · {d.hardware_family}
              <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${d.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{d.status}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("nfc.filterLabel")}</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm" data-testid="admin-nfc-status-filter">
          {STATUSES.map((s) => <option key={s || "all"} value={s}>{s ? `${s}${summary?.by_status?.[s] != null ? ` (${summary.by_status[s]})` : ""}` : t("nfc.filterAll")}</option>)}
        </select>
        <Dialog open={createOpen} onOpenChange={(v) => { if (v) openCreate(); else setCreateOpen(false); }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="nfc-create-test-btn" className="ml-auto rounded-full bg-navy text-white hover:bg-navy-muted"><Plus className="mr-1 h-4 w-4" /> {t("nfc.createTag")}</Button>
          </DialogTrigger>
          <DialogContent data-testid="nfc-create-dialog">
            <DialogHeader><DialogTitle>{t("nfc.createTag")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500">{t("nfc.assignCompany")}</label>
                <select value={selOrg} onChange={(e) => onSelectOrg(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="nfc-org-select">
                  <option value="">{t("nfc.optionalTest")}</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500">{t("nfc.assignSite")}</label>
                <select value={selSite} onChange={(e) => setSelSite(e.target.value)} disabled={!selOrg} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-50" data-testid="nfc-site-select">
                  <option value="">{selOrg && sites.length === 0 ? t("nfc.noSites") : t("nfc.selectSite")}</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {selOrg && (
                  <div className="mt-2 flex items-center gap-2">
                    <input value={newSiteName} onChange={(e) => setNewSiteName(e.target.value)} placeholder={t("nfc.newSitePlaceholder")} className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" data-testid="nfc-new-site-input" />
                    <Button size="sm" variant="outline" onClick={addSite} disabled={!newSiteName.trim()} data-testid="nfc-add-site-btn" className="rounded-full"><Plus className="mr-1 h-3.5 w-3.5" /> {t("nfc.addSite")}</Button>
                  </div>
                )}
              </div>
              {createdUrl ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="nfc-created-url">
                  <div className="mb-1 text-xs font-semibold text-emerald-700">{t("nfc.clockUrl")}</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-white px-2 py-1 text-xs text-slate-700">{createdUrl}</code>
                    <button onClick={() => copyUrl(createdUrl)} className="rounded-full bg-navy p-1.5 text-white" data-testid="nfc-copy-url" title={t("nfc.copyUrl")}><Copy className="h-3.5 w-3.5" /></button>
                    <a href={createdUrl} target="_blank" rel="noreferrer" className="rounded-full bg-slate-100 p-1.5 text-slate-600"><ExternalLink className="h-3.5 w-3.5" /></a>
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">{t("nfc.programHint")}</div>
                </div>
              ) : (
                <Button onClick={createTag} disabled={busyTag === "new"} className="w-full rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="nfc-create-submit">
                  {busyTag === "new" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} {t("nfc.createTag")}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admin-nfc-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["Tag", "Customer", "Baustelle", "Chip", "Status", "Scans", "Clocks", "Ver.", "v", ""].map((c, i) => <th key={i} className="px-3 py-3">{c}</th>)}</tr>
          </thead>
          <tbody>
            {!tags ? <tr><td colSpan={10} className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr> :
              tags.length === 0 ? <tr><td colSpan={10} className="p-8 text-center text-slate-400">{t("nfc.empty")}</td></tr> :
                tags.map((tg) => (
                  <Fragment key={tg.id}>
                    <tr className={`border-b border-slate-50 ${tg.status === "NEEDS_REPROGRAMMING" ? "bg-red-50/50" : ""}`} data-testid={`admin-nfc-${tg.nfc_tag_id}`}>
                      <td className="px-3 py-3 font-mono text-xs font-semibold text-navy">{tg.nfc_tag_id}</td>
                      <td className="px-3 py-3 text-slate-700">{tg.organization_name}</td>
                      <td className="px-3 py-3 text-slate-600">{tg.site_name}</td>
                      <td className="px-3 py-3 text-slate-500">{tg.chip_type}</td>
                      <td className="px-3 py-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge(tg.status)}`}>{tg.status === "NEEDS_REPROGRAMMING" && <AlertTriangle className="h-3 w-3" />}{tg.status}</span></td>
                      <td className="px-3 py-3 text-slate-600">{tg.total_scans}</td>
                      <td className="px-3 py-3 text-slate-600">{tg.successful_clock_events}</td>
                      <td className="px-3 py-3">{tg.write_verified ? "✓" : "—"}</td>
                      <td className="px-3 py-3 text-slate-400">v{tg.token_version}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => download(`/admin/hardware/tags/${tg.id}/qr.png`, `${tg.nfc_tag_id}.png`)} title={t("ai.qr")} data-testid={`admin-nfc-qr-${tg.nfc_tag_id}`} className="rounded-full bg-slate-100 p-1.5 text-slate-500 hover:bg-slate-200"><QrCode className="h-3.5 w-3.5" /></button>
                          <button onClick={() => download(`/admin/hardware/tags/${tg.id}/label.pdf`, `${tg.nfc_tag_id}_label.pdf`)} title={t("ai.label")} data-testid={`admin-nfc-label-${tg.nfc_tag_id}`} className="rounded-full bg-slate-100 p-1.5 text-slate-500 hover:bg-slate-200"><FileText className="h-3.5 w-3.5" /></button>
                          {(tg.status === "ACTIVE" || tg.status === "INACTIVE") && (
                            <button onClick={() => toggleActive(tg)} disabled={busyTag === tg.id} data-testid={`admin-nfc-toggle-${tg.nfc_tag_id}`}
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold ${tg.status === "ACTIVE" ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}>
                              {busyTag === tg.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
                              {tg.status === "ACTIVE" ? t("nfc.suspend") : t("nfc.reactivate")}
                            </button>
                          )}
                          <button onClick={() => setExpanded(expanded === tg.id ? null : tg.id)} className="text-slate-400 hover:text-navy" data-testid={`admin-nfc-expand-${tg.nfc_tag_id}`}><ChevronDown className={`h-4 w-4 transition-transform ${expanded === tg.id ? "rotate-180" : ""}`} /></button>
                        </div>
                      </td>
                    </tr>
                    {expanded === tg.id && (
                      <tr className="border-b border-slate-100 bg-slate-50/60" data-testid={`admin-nfc-meta-${tg.nfc_tag_id}`}>
                        <td colSpan={10} className="px-6 py-4">
                          <div className="grid gap-x-8 gap-y-2 text-xs text-slate-600 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5 text-slate-400" /> <span className="font-semibold">{t("nfc.chipUid")}:</span> {tg.chip_uid || "—"}</div>
                            <div className="flex items-center gap-1.5"><HardDrive className="h-3.5 w-3.5 text-slate-400" /> <span className="font-semibold">{t("nfc.memory")}:</span> {tg.memory_size_bytes ? `${tg.memory_size_bytes} B` : "—"}</div>
                            <div><span className="font-semibold">{t("nfc.method")}:</span> {tg.provisioning_method || "—"}</div>
                            <div><span className="font-semibold">{t("nfc.reader")}:</span> {tg.reader_type_used || "—"}</div>
                            <div><span className="font-semibold">{t("nfc.tagTypeLbl")}:</span> {tg.tag_type || "—"}</div>
                            <div><span className="font-semibold">{t("nfc.orderRef")}:</span> {tg.order_id ? tg.order_id.slice(0, 8) : "—"}</div>
                            <div><span className="font-semibold">{t("nfc.verifiedAt")}:</span> {fmt(tg.write_verified_at)}</div>
                            <div><span className="font-semibold">{t("nfc.verifiedBy")}:</span> {tg.write_verified_by || "—"}</div>
                            <div><span className="font-semibold">{t("nfc.generatedAt")}:</span> {fmt(tg.generated_at)}</div>
                            {tg.nfc_url && (
                              <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-2">
                                <span className="font-semibold">{t("nfc.clockUrl")}:</span>
                                <code className="rounded bg-white px-2 py-0.5 text-[11px] text-slate-700">{tg.nfc_url}</code>
                                <button onClick={() => copyUrl(tg.nfc_url)} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 hover:bg-slate-200" data-testid={`admin-nfc-copyurl-${tg.nfc_tag_id}`}><Copy className="h-3 w-3" /> {t("nfc.copyUrl")}</button>
                                <a href={tg.nfc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 hover:bg-slate-200"><ExternalLink className="h-3 w-3" /></a>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
